import { Component, ElementRef, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  FormControl,
  FormArray,
  AbstractControl,
  ValidatorFn,
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDatepickerModule } from '@angular/material/datepicker';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { PessoaService } from '../../../shared/services/apis/pessoa/pessoa.service';
import { ReturnStruct } from '../../../shared/services/structs/return.struct';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { PessoaResponse } from '../../../shared/services/responses/pessoa/pessoa.response';
import { environment } from '../../../../environments/environment';
import { PessoaLookupService } from '../../../shared/services/apis/pessoa/pessoa-lookup.service';
import { ConfigPessoaService } from '../../../shared/services/apis/pessoa/config-pessoa.service';
import { SelectPesquisavelComponent } from '../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { SelectPesquisavelService } from '../../../shared/components/select-pesquisavel/select-pesquisavel.service';
import { TipoPessoaEnum } from '../../../shared/enums/pessoa/tipo-pessoa.enum';
import { TipoContatoEnum } from '../../../shared/enums/pessoa/tipo-contato.enum';
import { VendaTipoDocumentoEnum } from '@shared/enums/vendas/venda-tipo-documento.enum';
import { CEPCorreios, UtilService } from '../../../shared/services/util.service';
import { ConsultaCnpjService } from '../../../shared/services/apis/pessoa/consulta-cnpj.service';
import {
  PageScrollSpyComponent,
  ScrollSpySection,
} from '../../../shared/components/page-scroll-spy/page-scroll-spy.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { EmptyStateComponent } from '../../../shared/components/empty-state/empty-state.component';
import { PessoaRequest } from '../../../shared/services/requests/pessoa/pessoa.request';
import { PessoaEnderecoStruct } from '../../../shared/services/structs/pessoa/pessoa-endereco.struct';
import { PessoaContatoStruct } from '../../../shared/services/structs/pessoa/pessoa-contato.struct';
import { LookupItemStruct } from '../../../shared/services/structs/pessoa/lookup-item.struct';
import { PessoaAnexoStruct } from '../../../shared/services/structs/pessoa/pessoa-anexo.struct';
import { MatDialog } from '@angular/material/dialog';
import {
  OportunidadesClienteDialogComponent,
  OportunidadesClienteDialogData,
} from '@shared/components/oportunidades-cliente-dialog/oportunidades-cliente-dialog.component';
import {
  UltimasVendasDialogComponent,
  UltimasVendasDialogData,
} from '@shared/components/ultimas-vendas-dialog/ultimas-vendas-dialog.component';
import {
  UltimasComprasDialogComponent,
  UltimasComprasDialogData,
} from '@shared/components/ultimas-compras-dialog/ultimas-compras-dialog.component';
import { ConfirmModalComponent } from '../../../shared/components/confirm-modal/confirm-modal.component';
import { Masks, MaskService } from '../../../shared/services/mask.service';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { FooterActionComponent } from '../../../shared/components/footer-action/footer-action.component';
import { NgxCurrencyDirective } from 'ngx-currency';
import { GetArquivoPessoaResponse } from '../../../shared/services/responses/pessoa/get-arquivo-pessoa.response';
import { cpfValidator } from '../../../shared/validators/cpf.validator';
import { cnpjValidator } from '../../../shared/validators/cnpj.validator';
import { AuthService } from '../../../shared/services/apis/usuario/auth.service';
import { MunicipioService } from '../../../shared/services/apis/usuario/municipio.service';
import { UnidadeFederativaService } from '../../../shared/services/apis/usuario/unidade-federativa.service';
import { MotorFiscalLookupService } from '../../../shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { LookupService as ProdutoLookupService } from '../../../shared/services/apis/produto/produto-lookup.service';
import { Editor, NgxEditorModule, Toolbar } from 'ngx-editor';
import { HeaderComponent } from '../../../shared/components/header/header.component';
import { PessoaConfiguracaoCampoObrigatorioService } from '@shared/services/apis/pessoa/pessoa-configuracao-campo-obrigatorio.service';
import { CampoObrigatorioStruct } from '@shared/services/structs/financas/campo-obrigatorio.struct';
import { TelaPessoaEnum } from '@shared/enums/pessoa/tela-pessoa.enum';
import { valorMaiorQueZeroValidator } from '@shared/validators/valor-maior-que-zero.validator';

@Component({
  selector: 'app-pessoa-cadastro',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    RouterModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatRadioModule,
    MatCheckboxModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MatMenuModule,
    MatDividerModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    SelectPesquisavelComponent,
    PageScrollSpyComponent,
    NgxMaskDirective,
    FooterActionComponent,
    NgxCurrencyDirective,
    HeaderComponent,
    NgxEditorModule,
    EmptyStateComponent,
  ],
  providers: [provideNgxMask()],
  templateUrl: './pessoa-cadastro.component.html',
  styleUrl: './pessoa-cadastro.component.css',
})
export class PessoaCadastroComponent implements OnInit, OnDestroy {
  public moduloEnum = ModuloEnum.Cadastros;
  public funcionalidadeEnum = FuncionalidadeEnum.Cadastros_Pessoas;

  public form: FormGroup;
  public isEditMode: boolean = false;
  public isLoading: boolean = false;
  public idPessoa: number | null = null;
  // Dados gravados da pessoa (do PessoaResponse carregado): as ações do menu Ações não seguem o form em edição (PA-11).
  public nomePessoaGravado: string = '';
  public listIdTipoContatoGravado: number[] = [];
  // O menu Ações só abre depois da pessoa carregada: antes disso as regras usariam tipos de contato vazios.
  public isPessoaGravadaCarregada: boolean = false;
  public isSaving: boolean = false;
  public isLoadingCep: boolean = false;
  public isLoadingCnpj: boolean = false;
  public masks: Masks = MaskService.getMasks();
  public currencyMask = this.maskService.getCurrencyMask();

  // URLs for Online Selects
  public get urlMunicipio(): string {
    return `${environment.urlApiUsuario}Municipio/select?idUnidadeFederativa=${this.form?.get('idUnidadeFederativa')?.value ?? 0}`;
  }

  public get urlMunicipioCobranca(): string {
    return `${environment.urlApiUsuario}Municipio/select?idUnidadeFederativa=${this.form?.get('idUnidadeFederativaCobranca')?.value ?? 0}`;
  }

  // Offline Lists (Lookups)
  public listaTipoPessoa: LookupItemStruct[];
  public listaUf: LookupItemStruct[];
  public listaPaises: LookupItemStruct[];
  public listaTipoContato: LookupItemStruct[];
  public listaIndicadorInscricaoEstadual: LookupItemStruct[];
  public listaRegimeTributario: LookupItemStruct[];
  public listaTipoTributario: LookupItemStruct[];
  public listaStatusCrm: LookupItemStruct[];
  public listaVendedores: LookupItemStruct[];
  public listaListaPreco: LookupItemStruct[] = [];
  private vincularVendedorPendente: boolean = false;
  public editor!: Editor;
  public toolbar: Toolbar = [
    ['bold', 'italic'],
    ['underline', 'strike'],
    ['ordered_list', 'bullet_list'],
    [{ heading: ['h1', 'h2', 'h3'] }],
    ['text_color', 'background_color'],
    ['align_left', 'align_center', 'align_right', 'align_justify'],
  ];
  public idUsuarioLogado: number = 0;

  // Campos obrigatórios configuráveis (Configurações > Cadastros > Campos obrigatórios).
  // Se a configuração não carregar, o formulário mantém só as obrigatoriedades fixas.
  public camposObrigatorios: CampoObrigatorioStruct[] = [];
  private readonly validadorLimiteCredito = valorMaiorQueZeroValidator();
  // nome_tecnico (coluna no banco) -> controle do formulário que recebe Validators.required.
  private readonly controlePorCampoConfiguravel: Record<string, string> = {
    cep: 'cep',
    logradouro: 'logradouro',
    bairro: 'bairro',
    id_regime_tributario: 'idRegimeTributario',
    telefone: 'telefone',
    id_vendedor: 'idVendedorResponsavel',
    nome_fantasia: 'nomeFantasia',
    data_nascimento: 'dataNascimento',
  };
  // Campos de endereço/fiscais nacionais: não se aplicam a pessoa estrangeira, mesmo configurados.
  private readonly camposNaoAplicaveisEstrangeiro: string[] = [
    'cep', 'logradouro', 'bairro', 'id_unidade_federativa', 'id_municipio', 'inscricao_estadual', 'id_regime_tributario',
  ];

  // Scroll Spy
  public sections: ScrollSpySection[] = [
    { id: 'info-basica', label: 'Informações Básicas' },
    { id: 'endereco', label: 'Endereços e Contatos' },
    { id: 'fiscal', label: 'Dados Fiscais' },
    { id: 'complementar', label: 'Dados Complementares' },
    { id: 'anexos', label: 'Anexos' },
  ];

  public preloadedCity: any[] = [];
  public preloadedBillingCity: any[] = [];
  public showBillingAddress: boolean = false;

  // Attachments
  public anexos: PessoaAnexoStruct[] = [];
  public readonly MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
  public readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 10MB

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private pessoaService: PessoaService,
    private configPessoaService: ConfigPessoaService,
    private pessoaLookupService: PessoaLookupService,
    private alertService: AlertService,
    private utilService: UtilService,
    private selectPesquisavelService: SelectPesquisavelService,
    private consultaCnpjService: ConsultaCnpjService,
    private dialog: MatDialog,
    private maskService: MaskService,
    private authService: AuthService,
    private municipioService: MunicipioService,
    private unidadeFederativaService: UnidadeFederativaService,
    private motorFiscalLookupService: MotorFiscalLookupService,
    private produtoLookupService: ProdutoLookupService,
    private pessoaConfiguracaoCampoObrigatorioService: PessoaConfiguracaoCampoObrigatorioService,
    private elementRef: ElementRef<HTMLElement>
  ) { }

  ngOnInit(): void {
    this.editor = new Editor();
    this.idUsuarioLogado = this.authService.getUserInfos().idUsuario;
    this.initForm();
    this.carregarCamposObrigatorios();
    this.loadVendedores();
    this.carregarLookups();
    this.msgIdPessoa();
  }

  ngOnDestroy(): void {
    this.editor.destroy();
  }

  private initForm(): void {
    this.form = this.fb.group({
      // Seção 1: Básicas
      isAtivo: [true],
      idTipoPessoa: [null, Validators.required],
      codigoCliente: [''],
      nome: ['', Validators.required],
      nomeFantasia: [''],
      cpf: new FormControl('', [cpfValidator()]),
      cnpj: new FormControl('', [cnpjValidator()]),
      documentoEstrangeiro: [''],
      idPais: [null],
      listIdTipoContato: [[], Validators.required],
      emailNFe: ['', [Validators.required, Validators.email]],
      website: [''],
      telefone: [''],

      // Seção 2: Endereço Principal
      cep: [''],
      logradouro: [''],
      numero: [''],
      bairro: [''],
      idMunicipio: [{ value: null, disabled: true }],
      idUnidadeFederativa: [null],
      complemento: [''],

      // Toggle Address
      hasBillingAddress: [false],

      // Seção 2.1: Endereço de Cobrança
      cepCobranca: [''],
      logradouroCobranca: [''],
      numeroCobranca: [''],
      bairroCobranca: [''],
      idMunicipioCobranca: [{ value: null, disabled: true }],
      idUnidadeFederativaCobranca: [null],
      complementoCobranca: [''],

      // Seção 2.2: Contatos Adicionais
      contatosAdicionais: this.fb.array([]),

      // Seção 3: Fiscais
      // Tipo Contribuinte (indIEDest): campo fixo da configuração de campos obrigatórios, exigido também para estrangeiro.
      idIndicadorInscricaoEstadual: [null, Validators.required],
      inscricaoEstadual: [''],
      inscricaoMunicipal: [''],
      idRegimeTributario: [null],
      idTipoTributario: [null],
      isConsumidorFinal: [false],
      isContribuinteIbsCbs: [false],
      inscricaoSuframa: [''],

      // Seção 4: Complementares
      dataNascimento: [null],
      datetimeInclusao: [{ value: null, disabled: true }],
      idVendedorResponsavel: [null],
      condicaoPagamento: [''],
      listIdListaPreco: [[]],
      observacoesGerais: [''],
      statusCrm: [null],
      limiteCredito: [0.0],
    });

    // Listeners
    this.form.get('idTipoPessoa')?.valueChanges.subscribe(val => this.onTipoPessoaChange(val));
    this.form.get('idIndicadorInscricaoEstadual')?.valueChanges.subscribe(val => this.onIndicadorInscricaoEstadualChange(val));
    this.form.get('hasBillingAddress')?.valueChanges.subscribe(val => {
      this.showBillingAddress = val;
      if (val) {
        // If turning on, maybe required validations? For now keep optional as per implied requirements
      }
    });

    this.loadDefaults(); // Load defaults on init
  }

  // Getters
  public get idPais(): FormControl {
    return this.form.get('idPais') as FormControl;
  }
  public get idUnidadeFederativa(): FormControl {
    return this.form.get('idUnidadeFederativa') as FormControl;
  }
  public get idMunicipio(): FormControl {
    return this.form.get('idMunicipio') as FormControl;
  }
  public get idUnidadeFederativaCobranca(): FormControl {
    return this.form.get('idUnidadeFederativaCobranca') as FormControl;
  }
  public get idMunicipioCobranca(): FormControl {
    return this.form.get('idMunicipioCobranca') as FormControl;
  }
  public get idIndicadorInscricaoEstadual(): FormControl {
    return this.form.get('idIndicadorInscricaoEstadual') as FormControl;
  }
  public get idRegimeTributario(): FormControl {
    return this.form.get('idRegimeTributario') as FormControl;
  }
  public get idTipoTributario(): FormControl {
    return this.form.get('idTipoTributario') as FormControl;
  }
  public get idVendedorResponsavel(): FormControl {
    return this.form.get('idVendedorResponsavel') as FormControl;
  }
  public get contatosAdicionais(): FormArray {
    return this.form.get('contatosAdicionais') as FormArray;
  }
  public get listIdListaPreco(): FormControl {
    return this.form.get('listIdListaPreco') as FormControl;
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event, this.form.get('dataNascimento'));
  }

  // File Upload Methods
  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.processFiles(input.files);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.add('drag-over');
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    (event.currentTarget as HTMLElement).classList.remove('drag-over');
    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.processFiles(files);
    }
  }

  processFiles(files: FileList): void {
    const currentTotalSize = this.anexos.reduce((acc, curr) => acc + (curr.tamanhoBytes || 0), 0);
    let newFilesSize = 0;

    // Calculate size of new files
    for (let i = 0; i < files.length; i++) {
      newFilesSize += files[i].size;
    }

    if (currentTotalSize + newFilesSize > this.MAX_TOTAL_SIZE) {
      this.alertService.show(
        'Aviso',
        'O tamanho total dos arquivos excede o limite de 10MB.',
        AlertType.warning
      );
      return;
    }

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > this.MAX_FILE_SIZE) {
        this.alertService.show(
          'Aviso',
          `O arquivo "${file.name}" excede o limite de 2MB.`,
          AlertType.warning
        );
        continue;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        const base64String = e.target.result.split(',')[1];
        this.anexos.push({
          nomeOriginal: file.name,
          tipoArquivo: file.type,
          tamanhoBytes: file.size,
          base64: base64String,
        });
      };
      reader.readAsDataURL(file);
    }
  }

  removeAnexo(index: number): void {
    this.anexos.splice(index, 1);
  }

  public downloadAnexo(index: number): void {
    const anexo = this.anexos[index];
    if (!anexo.urlCaminho) return;

    this.pessoaService.getArquivo(anexo.urlCaminho).subscribe({
      next: (response: GetArquivoPessoaResponse) => {
        if (response.isError) {
          this.alertService.show('Aviso', response.errorDescription, AlertType.error);
          return;
        }
        const link = document.createElement('a');
        link.href = response.urlArquivo;
        link.download = anexo.nomeOriginal; // Define o nome do arquivo ao baixar
        link.target = '_blank'; // Opcional: abre em nova aba se o download falhar

        // Adiciona ao DOM, clica e remove (necessário para Firefox)
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      },
      error: error => {
        this.alertService.show('Erro', 'Erro ao baixar arquivo.', AlertType.error);
      },
    });
  }

  limparAnexos(): void {
    const dialogRef = this.dialog.open(ConfirmModalComponent, {
      data: {
        title: 'Confirmação',
        description: 'Deseja realmente remover todos os anexos?',
        buttonConfirm: 'Sim',
        buttonClose: 'Não',
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.confirm) {
        this.anexos = [];
      }
    });
  }

  formatBytes(bytes: number | null | undefined, decimals = 2): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  }

  // Helpers for Contacts
  createContatoRow(data?: any): FormGroup {
    return this.fb.group({
      nome: [data?.nome || '', Validators.required],
      cargo: [data?.cargo || ''],
      email: [data?.email || '', [Validators.email]],
      telefone: [data?.telefone || ''],
      celular: [data?.celular || ''],
    });
  }

  addContato(): void {
    this.contatosAdicionais.push(this.createContatoRow());
  }

  removeContato(index: number): void {
    this.contatosAdicionais.removeAt(index);
  }

  private loadDefaults(): void {
    this.configPessoaService.getLookup().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }

        const config = res.configPessoa;

        if (!this.isEditMode) {
          if (config.idTipoPessoa) {
            this.form.patchValue({ idTipoPessoa: config.idTipoPessoa });
          }
          if (!this.idTipoContatoInclusao && res.listIdTipoContato && res.listIdTipoContato.length > 0) {
            this.form.patchValue({ listIdTipoContato: res.listIdTipoContato });
          }
          if (config.limiteCreditoPadrao !== undefined) {
            this.form.patchValue({ limiteCredito: config.limiteCreditoPadrao });
          }
          if (config.isVincularAutomaticamente) {
            if (this.listaVendedores) {
              this.atrelarVendedor();
            } else {
              // listaVendedores ainda não carregou (loadVendedores é assíncrono e independente);
              // marca como pendente para vincular assim que a lista chegar.
              this.vincularVendedorPendente = true;
            }
          }
        }
      },
    });
  }

  private msgIdPessoa(): void {
    const paramId = this.route.snapshot.paramMap.get('idPessoa');
    if (paramId) {
      this.idPessoa = Number(paramId);
      this.isEditMode = true;
      this.loadData(this.idPessoa);
      return;
    }
    if (this.idTipoContatoInclusao) this.form.patchValue({ listIdTipoContato: [this.idTipoContatoInclusao] });
  }

  /**
   * Perfil pedido por quem abriu a inclusão (ex.: "Cadastrar fornecedor" da NF-e de entrada usa
   * ?idTipoContato=2); substitui o perfil padrão da configuração e é ignorado na edição.
   */
  private get idTipoContatoInclusao(): TipoContatoEnum | null {
    const idTipoContato = Number(this.route.snapshot.queryParamMap.get('idTipoContato'));
    return TipoContatoEnum[idTipoContato] !== undefined ? idTipoContato as TipoContatoEnum : null;
  }

  private loadData(id: number): void {
    this.isLoading = true;
    this.pessoaService.getById(id).subscribe({
      next: (res: PessoaResponse) => {
        this.isLoading = false;
        if (res.isError || !res.pessoa) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          this.router.navigate(['/cadastros/pessoas']);
          return;
        }

        this.nomePessoaGravado = res.pessoa.nome;
        this.listIdTipoContatoGravado = res.listIdTipoContato ?? [];
        this.isPessoaGravadaCarregada = true;

        this.form.patchValue({
          ...res.pessoa,
          idVendedorResponsavel: res.pessoa.idVendedor,
          // Nomes divergem do model do backend (idStatusCrm/observacoes) —
          // o spread acima não preenche esses controles, precisa mapear explicitamente.
          statusCrm: res.pessoa.idStatusCrm,
          observacoesGerais: res.pessoa.observacoes,
        });
        if (res.pessoa.dataNascimento) {
          this.form.get('dataNascimento')?.setValue(new Date(res.pessoa.dataNascimento));
        }

        if (res.anexos) {
          this.anexos = res.anexos;
        }

        // Load Lists
        if (res.listIdTipoContato) this.form.patchValue({ listIdTipoContato: res.listIdTipoContato });
        if (res.listIdTipoPessoa) this.form.patchValue({ listIdTipoPessoa: res.listIdTipoPessoa });
        if (res.listIdListaPreco) this.form.patchValue({ listIdListaPreco: res.listIdListaPreco });

        // Load Addresses
        if (res.enderecos && res.enderecos.length > 0) {
          const principal = res.enderecos.find(x => x.isPrincipal);
          if (principal) {
            this.form.patchValue({
              cep: principal.cep,
              logradouro: principal.logradouro,
              numero: principal.numero,
              bairro: principal.bairro,
              complemento: principal.complemento,
              idUnidadeFederativa: principal.idUnidadeFederativa,
            });
            this.atualizarEstadoMunicipio(principal.idUnidadeFederativa, false, false);
            this.form.get('idMunicipio')?.setValue(principal.idMunicipio);
            this.carregarMunicipioPreload(principal.idUnidadeFederativa, principal.idMunicipio);
          }

          const cobranca = res.enderecos.find(x => x.isCobranca);
          if (cobranca) {
            this.form.patchValue({
              hasBillingAddress: true,
              cepCobranca: cobranca.cep,
              logradouroCobranca: cobranca.logradouro,
              numeroCobranca: cobranca.numero,
              bairroCobranca: cobranca.bairro,
              complementoCobranca: cobranca.complemento,
              idUnidadeFederativaCobranca: cobranca.idUnidadeFederativa,
            });
            this.showBillingAddress = true;
            this.atualizarEstadoMunicipio(cobranca.idUnidadeFederativa, true, false);
            this.form.get('idMunicipioCobranca')?.setValue(cobranca.idMunicipio);
            this.carregarMunicipioPreload(cobranca.idUnidadeFederativa, cobranca.idMunicipio, true);
          }
        }

        // Load Contacts
        if (res.contatosAdicionais && res.contatosAdicionais.length > 0) {
          res.contatosAdicionais.forEach(c => {
            this.contatosAdicionais.push(this.createContatoRow(c));
          });
        }

        if (res.pessoa.idTipoPessoa) {
          this.onTipoPessoaChange(res.pessoa.idTipoPessoa);
        }
      },
      error: err => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar o cadastro.', AlertType.error);
        this.router.navigate(['/cadastros/pessoas']);
      },
    });
  }

  // Motivo de as ações de CRM ficarem indisponíveis (PA-01); vazio = habilitadas.
  getMotivoCrmIndisponivel(listIdTipoContato: number[]): string {
    const ids = listIdTipoContato ?? [];
    if (!ids.length || !ids.every(id => id === TipoContatoEnum.Fornecedor || id === TipoContatoEnum.Transportadora)) {
      return '';
    }
    const isFornecedor = ids.includes(TipoContatoEnum.Fornecedor);
    const isTransportadora = ids.includes(TipoContatoEnum.Transportadora);
    const quem = isFornecedor && isTransportadora ? 'fornecedor e transportadora' : isFornecedor ? 'fornecedor' : 'transportadora';
    return `Pessoa cadastrada só como ${quem}. Oportunidades no CRM são para clientes: inclua o tipo Cliente no cadastro para usar.`;
  }

  abrirOportunidadesCrm(): void {
    const data: OportunidadesClienteDialogData = {
      idPessoaCliente: this.idPessoa!,
      nomeCliente: this.nomePessoaGravado,
      titulo: 'Oportunidades no CRM',
      origem: 'pessoa',
    };
    this.dialog.open(OportunidadesClienteDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      autoFocus: false,
      data,
    });
  }

  incluirOportunidadeCrm(): void {
    this.router.navigate(['/vendas/crm'], {
      queryParams: { novaOportunidadeIdPessoaCliente: this.idPessoa, origemNovaOportunidade: 'pessoa' },
    });
  }

  // Motivo de orçamento e pedido ficarem indisponíveis (PA-02): só para quem não é Cliente; vazio = habilitados.
  getMotivoOrcamentoPedidoIndisponivel(listIdTipoContato: number[]): string {
    const ids = listIdTipoContato ?? [];
    if (!ids.length || ids.includes(TipoContatoEnum.Cliente)) return '';
    const isFornecedor = ids.includes(TipoContatoEnum.Fornecedor);
    const isTransportadora = ids.includes(TipoContatoEnum.Transportadora);
    const quem = isFornecedor && isTransportadora ? 'fornecedor e transportadora' : isFornecedor ? 'fornecedor' : 'transportadora';
    return `Pessoa cadastrada só como ${quem}. Orçamentos e pedidos de venda são para clientes: `
      + 'inclua o tipo Cliente no cadastro para usar.';
  }

  // Motivo de "Criar nota fiscal" ficar indisponível (PA-02); vazio = habilitada.
  getMotivoNotaFiscalIndisponivel(listIdTipoContato: number[]): string {
    const ids = listIdTipoContato ?? [];
    if (ids.includes(TipoContatoEnum.Transportadora)
      && !ids.includes(TipoContatoEnum.Cliente) && !ids.includes(TipoContatoEnum.Fornecedor)) {
      return 'Transportadora não recebe nota fiscal. Inclua o tipo Cliente ou Fornecedor no cadastro para emitir.';
    }
    return '';
  }

  incluirOrcamento(): void {
    this.router.navigate(['/vendas/processo-venda/documento'], {
      queryParams: { tipo: VendaTipoDocumentoEnum.Orcamento, idPessoaCliente: this.idPessoa },
    });
  }

  criarPedidoVenda(): void {
    this.router.navigate(['/vendas/processo-venda/documento'], {
      queryParams: { tipo: VendaTipoDocumentoEnum.PedidoDeVenda, idPessoaCliente: this.idPessoa },
    });
  }

  criarNotaFiscal(): void {
    this.router.navigate(['/vendas/nota-fiscal-saida/documento'], {
      queryParams: { idPessoaDestinatario: this.idPessoa },
    });
  }

  abrirUltimasVendas(): void {
    const data: UltimasVendasDialogData = { idPessoa: this.idPessoa!, nomePessoa: this.nomePessoaGravado };
    this.dialog.open(UltimasVendasDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      height: '90vh',
      autoFocus: false,
      data,
    });
  }

  abrirUltimasCompras(): void {
    const data: UltimasComprasDialogData = { idPessoa: this.idPessoa!, nomePessoa: this.nomePessoaGravado };
    this.dialog.open(UltimasComprasDialogComponent, {
      width: '1040px',
      maxWidth: '96vw',
      maxHeight: '90vh',
      height: '90vh',
      autoFocus: false,
      data,
    });
  }

  private carregarLookups(): void {
    this.pessoaLookupService.getLookup().subscribe({
      next: res => {
        if (!res.isError) {
          this.listaTipoPessoa = res.listaTipoPessoa;
          this.listaPaises = res.listaPais;
          this.listaTipoContato = res.listaTipoContato;
          this.listaStatusCrm = res.listaStatusCrm;
        }
      },
    });

    this.unidadeFederativaService.getUnidadesFederativas().subscribe(lista => {
      this.listaUf = lista.map(x => ({
        id: x.idUnidadeFederativa,
        descricao: x.descricaoExibicao,
        codigo: x.id,
      }));
    });
    this.motorFiscalLookupService.getIndicadoresInscricaoEstadual().subscribe(lista => this.listaIndicadorInscricaoEstadual = lista);
    this.motorFiscalLookupService.getRegimesTributarios().subscribe(lista => this.listaRegimeTributario = lista);
    this.motorFiscalLookupService.getTiposTributario().subscribe(lista => this.listaTipoTributario = lista);

    this.produtoLookupService.getListaPrecoSelect().subscribe(res => {
      if (!res.isError) this.listaListaPreco = res.list || [];
    });
  }

  private carregarMunicipioPreload(idUf: number, idMunicipio: number, isBilling: boolean = false): void {
    this.municipioService.getByUf(idUf).subscribe(res => {
      const municipio = res.list.find(x => x.idMunicipio === idMunicipio);
      if (municipio) {
        if (isBilling) this.preloadedBillingCity = [municipio];
        else this.preloadedCity = [municipio];
      }
    });
  }

  // Logic Handlers

  onTipoPessoaChange(tipo: number): void {
    const cpfControl = this.form.get('cpf');
    const cnpjControl = this.form.get('cnpj');
    const docExtControl = this.form.get('documentoEstrangeiro');
    const paisControl = this.form.get('idPais');

    // Reset validators first
    cpfControl?.clearValidators();
    cnpjControl?.clearValidators();
    docExtControl?.clearValidators();

    if (tipo === TipoPessoaEnum.Fisica) {
      cpfControl?.enable();
      cpfControl?.setValidators([Validators.required, cpfValidator()]); // Re-add validator
      cnpjControl?.disable();
      cnpjControl?.setValue(null);
      docExtControl?.disable();
      docExtControl?.setValue(null);
      paisControl?.disable();
      paisControl?.setValue(null);
    } else if (tipo === TipoPessoaEnum.Juridica) {
      cpfControl?.disable();
      cpfControl?.setValue(null);
      cnpjControl?.enable();
      cnpjControl?.setValidators([Validators.required, cnpjValidator()]); // Re-add validator
      docExtControl?.disable();
      docExtControl?.setValue(null);
      paisControl?.disable();
      paisControl?.setValue(null);
    } else if (tipo === TipoPessoaEnum.Estrangeiro) {
      // "Ao trocar para 'Estrangeiro', os campos CPF e CNPJ devem permanecer visíveis, mas com edição bloqueada."
      // User Story text says: "Se Pessoa Estrangeiro. Habilitar Doc. Identificação Bloquear CPF e CNPJ."
      // But also "Dinâmica de campos... nunca devem ser ocultados, apenas alternar entre Habilitado e Bloqueado"
      cpfControl?.disable();
      cpfControl?.setValue(null);
      cnpjControl?.disable();
      cnpjControl?.setValue(null);
      docExtControl?.enable();
      docExtControl?.setValidators([Validators.required]);
      paisControl?.enable();
    }

    cpfControl?.updateValueAndValidity();
    cnpjControl?.updateValueAndValidity();
    docExtControl?.updateValueAndValidity();

    this.aplicarCamposObrigatorios();
  }

  onIndicadorInscricaoEstadualChange(tipo: number): void {
    if (tipo === 1) {
      // 1 = ICMS Contributor
      this.form.get('inscricaoEstadual')?.enable();
    }
    this.aplicarObrigatoriedadeInscricaoEstadual();
  }

  private carregarCamposObrigatorios(): void {
    this.pessoaConfiguracaoCampoObrigatorioService.getByTela(TelaPessoaEnum.Pessoa).subscribe({
      next: res => {
        if (res.isError) return;
        this.camposObrigatorios = res.listaTelaComCampos[0]?.listaCampos ?? [];
        this.aplicarCamposObrigatorios();
      },
      // Falha na configuração não bloqueia o cadastro: seguem só as obrigatoriedades fixas.
      error: () => { },
    });
  }

  public isObrigatorio(nomeTecnico: string): boolean {
    if (this.camposNaoAplicaveisEstrangeiro.includes(nomeTecnico)
      && this.form?.get('idTipoPessoa')?.value === TipoPessoaEnum.Estrangeiro) {
      return false;
    }
    return this.camposObrigatorios.some(c => c.nomeTecnico === nomeTecnico && c.isObrigatorio);
  }

  private aplicarCamposObrigatorios(): void {
    if (!this.form) return;

    Object.entries(this.controlePorCampoConfiguravel).forEach(([nomeTecnico, controle]) =>
      this.alternarValidador(this.form.get(controle), Validators.required, this.isObrigatorio(nomeTecnico)));

    // Município só é editável depois da UF (controle desabilitado não valida), então exigir Município também exige UF.
    this.alternarValidador(this.form.get('idUnidadeFederativa'), Validators.required,
      this.isObrigatorio('id_unidade_federativa') || this.isObrigatorio('id_municipio'));
    this.alternarValidador(this.form.get('idMunicipio'), Validators.required, this.isObrigatorio('id_municipio'));

    // Limite de crédito começa em 0: quando obrigatório, exige valor maior que zero.
    this.alternarValidador(this.form.get('limiteCredito'), this.validadorLimiteCredito, this.isObrigatorio('limite_credito'));

    this.aplicarObrigatoriedadeInscricaoEstadual();
  }

  /** Inscrição Estadual: obrigatória para contribuinte de ICMS (regra fixa) ou quando configurada. */
  private aplicarObrigatoriedadeInscricaoEstadual(): void {
    const isContribuinte = this.form.get('idIndicadorInscricaoEstadual')?.value === 1;
    this.alternarValidador(this.form.get('inscricaoEstadual'), Validators.required,
      isContribuinte || this.isObrigatorio('inscricao_estadual'));
  }

  private alternarValidador(control: AbstractControl | null, validador: ValidatorFn, exigir: boolean): void {
    if (!control) return;
    if (exigir && !control.hasValidator(validador)) {
      control.addValidators(validador);
    } else if (!exigir && control.hasValidator(validador)) {
      control.removeValidators(validador);
    }
    control.updateValueAndValidity({ emitEvent: false });
  }

  /** Rola até o primeiro campo inválido e o foca. */
  private focarPrimeiroCampoInvalido(): void {
    setTimeout(() => {
      const campo = this.elementRef.nativeElement.querySelector<HTMLElement>(
        'input.ng-invalid, textarea.ng-invalid, mat-select.ng-invalid');
      campo?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      campo?.focus({ preventScroll: true });
    });
  }

  buscarCep(isBilling: boolean = false): void {
    const cepControl = isBilling ? this.form.get('cepCobranca') : this.form.get('cep');
    const cep = cepControl?.value;

    if (!cep || cep.length < 8) return;

    this.isLoadingCep = true;
    this.utilService.getAddressByCEP(cep).subscribe({
      next: (res: CEPCorreios) => {
        this.isLoadingCep = false;
        if (!res || !res.cep) {
          this.alertService.show('Aviso', 'CEP não encontrado.', AlertType.warning);
          return;
        }

        const ufMatch = this.listaUf.find(u => u.codigo === res.uf);
        const paisBrasil = this.listaPaises.find(p => p.descricao.toLowerCase() === 'brasil');

        const patchData: any = {};
        const fieldSuffix = isBilling ? 'Cobranca' : '';

        patchData[`logradouro${fieldSuffix}`] = res.logradouro;
        patchData[`bairro${fieldSuffix}`] = res.bairro;
        patchData[`idUnidadeFederativa${fieldSuffix}`] = ufMatch ? ufMatch.id : null;

        if (!isBilling && paisBrasil) {
          this.form.patchValue({ idPais: paisBrasil.id });
        }

        this.form.patchValue(patchData);
        this.atualizarEstadoMunicipio(ufMatch?.id ?? null, isBilling);

        if (res.localidade) {
          const url = isBilling ? this.urlMunicipioCobranca : this.urlMunicipio;
          this.selectPesquisavelService.getSelectValues<any>(url, res.localidade).subscribe({
            next: cidades => {
              const cidadeMatch = cidades.find(c => c.codIbge === res.ibge);
              if (cidadeMatch) {
                if (!isBilling) {
                  this.preloadedCity = [cidadeMatch];
                } else {
                  this.preloadedBillingCity = [cidadeMatch];
                }
                const cityField = isBilling ? 'idMunicipioCobranca' : 'idMunicipio';
                this.form.get(cityField)?.setValue(cidadeMatch.idMunicipio);
              }
            },
          });
        }
      },
      error: () => (this.isLoadingCep = false),
    });
  }

  consultarCnpj(cnpj: string): void {
    this.isLoadingCnpj = true;
    this.consultaCnpjService.consultarCnpj(cnpj).subscribe({
      next: res => {
        this.isLoadingCnpj = false;
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        const dados = res.consultaCnpjStruct;
        this.form.patchValue({
          nome: dados.nome,
          nomeFantasia: dados.fantasia,
          cep: dados.cep,
          logradouro: dados.logradouro,
          numero: dados.numero,
          complemento: dados.complemento,
          bairro: dados.bairro,
          emailNFe: dados.email,
        });

        const ufMatch = this.listaUf?.find(u => u.descricao === dados.uf);
        if (ufMatch) {
          this.form.patchValue({ idUnidadeFederativa: ufMatch.id });

          if (dados.municipio) {
            this.selectPesquisavelService.getSelectValues<any>(this.urlMunicipio, dados.municipio).subscribe({
              next: cidades => {
                const cidadeMatch = cidades.find(
                  c => c.nome?.toLowerCase() === dados.municipio.toLowerCase()
                ) ?? cidades.find(c => c.nome?.toLowerCase().includes(dados.municipio.toLowerCase()));

                if (cidadeMatch) {
                  this.preloadedCity = [cidadeMatch];
                  this.form.patchValue({ idMunicipio: cidadeMatch.idMunicipio });
                }
              },
            });
          }
        }
      },
      error: () => (this.isLoadingCnpj = false),
    });
  }

  changeCidade(item: any, isBilling: boolean = false) {
    if (item?.idUnidadeFederativa) {
      const ufMatch = this.listaUf.find(u => u.id === item.idUnidadeFederativa);
      if (ufMatch) {
        const field = isBilling ? 'idUnidadeFederativaCobranca' : 'idUnidadeFederativa';
        this.form.patchValue({ [field]: ufMatch.id });

        // If not billing, verify if we should assume Country is Brazil (common case)
        if (!isBilling) {
          const paisBrasil = this.listaPaises.find(p => p.descricao.toLowerCase() === 'brasil');
          if (paisBrasil && !this.form.get('idPais')?.value) {
            this.form.patchValue({ idPais: paisBrasil.id });
          }
        }
      }
    }
  }

  changeUnidadeFederativa(item: any, isBilling: boolean = false): void {
    this.atualizarEstadoMunicipio(item?.id ?? null, isBilling);
  }

  private atualizarEstadoMunicipio(idUf: number | null, isBilling: boolean, limpar: boolean = true): void {
    const municipioControl = this.form.get(isBilling ? 'idMunicipioCobranca' : 'idMunicipio');

    if (limpar) {
      municipioControl?.setValue(null);
      if (isBilling) this.preloadedBillingCity = [];
      else this.preloadedCity = [];
    }

    if (idUf) municipioControl?.enable({ emitEvent: false });
    else municipioControl?.disable({ emitEvent: false });
  }

  salvar(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.alertService.show('Atenção', 'Há campos obrigatórios não preenchidos ou inválidos. Verifique os campos destacados.', AlertType.warning);
      this.focarPrimeiroCampoInvalido();
      return;
    }

    // Prepare Request
    // We need to map the flat form structure to the nested objects expected by API
    const formVal = this.form.getRawValue();

    if ((formVal.cep || formVal.logradouro) && (!formVal.idMunicipio || !formVal.idUnidadeFederativa)) {
      this.alertService.show('Atenção', 'Informe cidade e UF do endereço principal.', AlertType.warning);
      return;
    }

    if (formVal.hasBillingAddress && (!formVal.idMunicipioCobranca || !formVal.idUnidadeFederativaCobranca)) {
      this.alertService.show('Atenção', 'Informe cidade e UF do endereço de cobrança.', AlertType.warning);
      return;
    }

    this.isSaving = true;

    // Construct Addresses
    const enderecos: PessoaEnderecoStruct[] = [];

    // Principal
    if (formVal.cep || formVal.logradouro) {
      enderecos.push({
        cep: formVal.cep,
        logradouro: formVal.logradouro,
        numero: formVal.numero,
        bairro: formVal.bairro,
        complemento: formVal.complemento,
        idMunicipio: formVal.idMunicipio,
        idUnidadeFederativa: formVal.idUnidadeFederativa,
        isPrincipal: true,
        isCobranca: false,
      });
    }

    // Cobranca
    if (formVal.hasBillingAddress) {
      enderecos.push({
        cep: formVal.cepCobranca,
        logradouro: formVal.logradouroCobranca,
        numero: formVal.numeroCobranca,
        bairro: formVal.bairroCobranca,
        complemento: formVal.complementoCobranca,
        idMunicipio: formVal.idMunicipioCobranca,
        idUnidadeFederativa: formVal.idUnidadeFederativaCobranca,
        isPrincipal: false,
        isCobranca: true,
      });
    }

    const request: PessoaRequest = {
      ...formVal,
      idVendedor: formVal.idVendedorResponsavel,
      // Nomes divergem do que o backend espera (statusCrm/observacoesGerais) —
      // o spread acima não preenche idStatusCrm/observacoes, precisa mapear explicitamente.
      idStatusCrm: formVal.statusCrm,
      observacoes: formVal.observacoesGerais,
      enderecos: enderecos,
      // Map other specific fields if they don't match exactly by name or need transform
      listIdCondicaoPagamento: [],
      contatosAdicionais: formVal.contatosAdicionais, // Already in struct format due to FormArray structure match?
      anexos: this.anexos,
    };

    if (this.isEditMode && this.idPessoa) {
      this.pessoaService.update(this.idPessoa, request).subscribe({
        next: res => this.handleReturn(res),
        error: err => this.handleError(err),
      });
    } else {
      this.pessoaService.create(request).subscribe({
        next: res => this.handleReturn(res),
        error: err => this.handleError(err),
      });
    }
  }

  private handleReturn(res: ReturnStruct): void {
    this.isSaving = false;
    if (res.isError) {
      this.alertService.show('Erro', res.errorDescription, AlertType.error);
    } else {
      this.alertService.show('Sucesso', 'Pessoa salva com sucesso.', AlertType.success);
      this.router.navigate(['/cadastros/pessoas']);
    }
  }

  private handleError(err: any): void {
    this.isSaving = false;
    this.alertService.show('Erro', 'Ocorreu um erro ao salvar.', AlertType.error);
  }

  private loadVendedores(): void {
    this.pessoaLookupService.listVendedorSelect().subscribe({
      next: res => {
        if (res.isError) {
          this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
          return;
        }
        this.listaVendedores = res.list;

        if (this.vincularVendedorPendente) {
          this.vincularVendedorPendente = false;
          this.atrelarVendedor();
        }
      },
      error: err => this.handleError(err),
    });
  }

  cancelar(): void {
    this.router.navigate(['/cadastros/pessoas']);
  }

  atrelarVendedor(): void {
    const vendedor = this.listaVendedores?.find(
      v => v.codigo == this.authService.getUserInfos().idUsuario?.toString()
    );
    this.form.patchValue({ idVendedorResponsavel: vendedor?.id });
  }
}
